import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Paper,
  useTheme,
  alpha,
  Skeleton,
  Avatar,
  Stack,
  Chip,
} from '@mui/material';
import Filters from '../../components/Filters/Filters';
import { VocuherCategory } from '../../utils/common-utils';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { verifyInviteToken } from '../../store/userThunk';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  fetchARDashboardNumbers,
  fetchARDashboardSales,
  fetchARDashboardAging,
  fetchARDashboardCustomersDue
} from '../../store/ARDashboardStore/ARDashboardThunk';
import {
  fetchAPDashboardNumbers,
  fetchAPDashboardSales,
  fetchAPDashboardCustomersDue
} from '../../store/APDashboardStore/APDashboardThunk';
import { ApiStatus } from '../../utils/api.types';
import { Utils } from '../../utils/common-utils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InsightsIcon from '@mui/icons-material/Insights';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import RefreshIcon from '@mui/icons-material/Refresh';
import Chart from 'react-apexcharts';
import Button from '@mui/material/Button';
import BusinessSummarySend from '../../components/BusinessSummary/BusinessSummarySend';

const ACCENT = '#84cc16';
const ACCENT_LIGHT = '#d9f99d';
const ACCENT_SOFT = '#ecfccb';
const BG = '#fafafb';
const SURFACE = '#ffffff';
const TEXT = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#cbd5e1';

const cardSx = {
  borderRadius: 4,
  border: `1px solid ${BORDER}`,
  boxShadow: 'none',
  backgroundColor: SURFACE
};

// Compact currency — Cr for >=1 Cr, else L (Lakh)
const fmtShort = (num: number): string => {
  if (num == null || isNaN(num)) return '₹ 0';
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 10000000) return `${sign}₹ ` + (abs / 10000000).toFixed(2).replace(/\.?0+$/, '') + ' Cr';
  return `${sign}₹ ` + (abs / 100000).toFixed(2).replace(/\.?0+$/, '') + ' L';
};

// Smart currency — full grouped amount if <1 Cr, Cr suffix if >=1 Cr
const fmtSmart = (num: number): string => Utils.formatSmart(num);

const Home = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, selectedAccount } = useAppSelector(state => state.auth);
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [filterParams, setFilterParams] = useState<string>('');

  const handleFilterChange = (params: string) => {
    setFilterParams(params);
  };

  const ARDashboard = useAppSelector(state => state.ARDashboard);
  const APDashboard = useAppSelector(state => state.APDashboard);

  useEffect(() => {
    const token = searchParams.get('inviteToken');
    if (token) {
      dispatch(verifyInviteToken({ token }));
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (selectedAccount?._id && filterParams) {
      fetchDashboardData(filterParams);
    }
  }, [selectedAccount, filterParams]);

  const fetchDashboardData = async (filters: string) => {
    if (!selectedAccount?._id) return;
    setIsLoading(true);

    try {
      const arPromises = [
        dispatch(fetchARDashboardNumbers({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardSales({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardAging({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardCustomersDue({ accountId: selectedAccount._id, params: filters }))
      ];

      const apPromises = [
        dispatch(fetchAPDashboardNumbers({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardSales({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardCustomersDue({ accountId: selectedAccount._id, params: filters }))
      ];

      await Promise.allSettled([...arPromises, ...apPromises]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const MetricCard = ({
    title,
    value,
    trend,
    icon,
    accent = ACCENT,
    highlight = false
  }: {
    title: string;
    value: string | number;
    trend?: { hasGrown: boolean; value: number };
    icon: React.ReactNode;
    accent?: string;
    highlight?: boolean;
  }) => (
    <Card
      sx={{
        ...cardSx,
        backgroundColor: highlight ? ACCENT_SOFT : SURFACE,
        borderColor: highlight ? ACCENT_LIGHT : BORDER,
        height: '100%',
        minHeight: 90,
        transition: 'transform .2s ease, box-shadow .2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px -12px rgba(15,23,42,0.12)'
        }
      }}
    >
      <CardContent sx={{
        p: 1.75,
        '&:last-child': { pb: 1.75 },
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{ color: MUTED, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.3, mb: 0.75 }}
              noWrap
            >
              {title}
            </Typography>
            <Typography
              sx={{ fontWeight: 700, color: TEXT, lineHeight: 1.2, fontSize: '1.15rem' }}
              noWrap
            >
              {isLoading ? <Skeleton width={70} /> : value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: highlight ? '#ffffff' : alpha(accent, 0.12),
              color: accent,
              flexShrink: 0
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 17, color: accent } })}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  // Merge AR + AP series onto same date labels so both series align on the x-axis
  const buildAlignedSeries = (
    arSeries: Record<string, any> | null | undefined,
    apSeries: Record<string, any> | null | undefined,
    bucketCount: number = 12
  ) => {
    // Backend sends keys like "2026-6-16" (no leading zeros) — normalize to "2026-06-16"
    const normalizeKey = (k: string) => {
      const parts = k.split('-');
      if (parts.length !== 3) return k;
      return `${parts[0]}-${parts[1].padStart(2,'0')}-${parts[2].padStart(2,'0')}`;
    };

    const toEntries = (s: Record<string, any> | null | undefined) =>
      s ? Object.entries(s).map(([k, v]) => ({ date: normalizeKey(k), value: Number(v) || 0 })) : [];

    const arEntries = toEntries(arSeries);
    const apEntries = toEntries(apSeries);

    const allDates = Array.from(new Set([...arEntries.map(e => e.date), ...apEntries.map(e => e.date)])).sort();
    if (allDates.length === 0) return { labels: [], arValues: [], apValues: [] };

    const arMap = Object.fromEntries(arEntries.map(e => [e.date, e.value]));
    const apMap = Object.fromEntries(apEntries.map(e => [e.date, e.value]));

    const bucketSize = allDates.length > bucketCount ? Math.ceil(allDates.length / bucketCount) : 1;
    const labels: string[] = [];
    const arValues: number[] = [];
    const apValues: number[] = [];

    for (let i = 0; i < allDates.length; i += bucketSize) {
      const chunk = allDates.slice(i, i + bucketSize);
      const parts = chunk[0].split('-');
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      labels.push(d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }));
      arValues.push(chunk.reduce((s, k) => s + (arMap[k] || 0), 0));
      apValues.push(chunk.reduce((s, k) => s + (apMap[k] || 0), 0));
    }

    return { labels, arValues, apValues };
  };

  const { labels: trendCategories, arValues: arTrendValues, apValues: apTrendValues } = buildAlignedSeries(
    ARDashboard.sales?.dashboardSales?.series,
    APDashboard.sales?.dashboardSales?.series,
    12
  );

  const salesChartOptions = {
    chart: {
      type: 'line' as const,
      height: 340,
      toolbar: { show: false },
      fontFamily: 'inherit',
      stacked: false,
      animations: { enabled: true, easing: 'easeinout' as const, speed: 600 }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 6,
        borderRadiusApplication: 'end' as const
      }
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: [0, 3], curve: 'smooth' as const },
    xaxis: {
      categories: trendCategories,
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      min: 0,
      labels: {
        style: { colors: MUTED, fontSize: '11px' },
        formatter: (value: number) => value < 0 ? '' : Utils.formatCompact(value, { space: false })
      }
    },
    colors: ['#059669', '#f59e0b'],
    fill: {
      opacity: [0.95, 1],
      type: ['solid', 'solid']
    },
    markers: {
      size: [0, 7],
      colors: ['#f59e0b'],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: { size: 9 }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) }
    },
    grid: {
      borderColor: BORDER,
      strokeDashArray: 4,
      padding: { left: 8, right: 8 },
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } }
    },
    legend: {
      position: 'top' as const,
      horizontalAlign: 'right' as const,
      fontSize: '12px',
      markers: { width: 10, height: 10, radius: 12 } as any,
      labels: { colors: MUTED },
      itemMargin: { horizontal: 12, vertical: 0 }
    }
  };

  const salesChartSeries = [
    { name: 'Sales Revenue', type: 'column', data: arTrendValues },
    { name: 'Purchase Amount', type: 'line', data: apTrendValues }
  ];

  // Aging Chart - simple pie with side total
  const agingChartOptions = {
    chart: { type: 'donut' as const, height: 220, fontFamily: 'inherit' },
    labels: ARDashboard.aging?.dashboardAging?.series ?
      Object.keys(ARDashboard.aging.dashboardAging.series) : [],
    colors: [ACCENT, '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6'],
    dataLabels: {
      enabled: true,
      style: { fontSize: '11px', fontWeight: 700, colors: ['#fff'] },
      formatter: (val: number) => `${val.toFixed(0)}%`,
      dropShadow: { enabled: false }
    },
    stroke: { width: 2, colors: ['#fff'] },
    legend: {
      position: 'bottom' as const,
      horizontalAlign: 'center' as const,
      fontSize: '11px',
      markers: { width: 8, height: 8, radius: 12 } as any,
      labels: { colors: MUTED },
      itemMargin: { horizontal: 6, vertical: 2 }
    },
    plotOptions: {
      pie: {
        donut: { size: '60%', labels: { show: false } }
      }
    },
    tooltip: {
      y: { formatter: (val: number) => Utils.formatNumber(val, true, false) }
    }
  };

  const agingChartSeries = ARDashboard.aging?.dashboardAging?.series ?
    Object.values(ARDashboard.aging.dashboardAging.series).map(val => Number(val) || 0) : [];

  // Sales vs Received - use numbers totalAR + toCollect (balance) for accurate totals
  const arNums: any = ARDashboard.numbers?.dashboardNumbers || {};
  const totalSales = Number(arNums.totalAR || 0);
  const outstandingAR = Number(arNums.toCollect || arNums.arUnpaid || 0);
  const arFullyPaidAmt = Number(arNums.arFullyPaid || 0);
  const arPartiallyPaidReceivedAmt = Number(arNums.arPartiallyPaidReceived || 0);
  const amountReceived = Number(arNums.arPaid || (arFullyPaidAmt + arPartiallyPaidReceivedAmt) || 0);

  const barBase = {
    chart: { type: 'bar' as const, height: 340, toolbar: { show: false }, fontFamily: 'inherit' },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45%',
        borderRadius: 8,
        borderRadiusApplication: 'end' as const,
        distributed: true,
        dataLabels: { position: 'top' as const }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => Utils.formatCompact(val),
      offsetY: -22,
      style: { fontSize: '11px', fontWeight: 700, colors: [TEXT] }
    },
    yaxis: {
      min: 0,
      tickAmount: 4,
      labels: {
        style: { colors: MUTED, fontSize: '11px' },
        formatter: (value: number) => value < 0 ? '' : Utils.formatCompact(value)
      },
      forceNiceScale: true,
      max: (max: number) => Math.ceil(max * 1.25 / 100) * 100
    },
    legend: { show: false },
    tooltip: { y: { formatter: (value: number) => Utils.formatNumber(value, true, false) } },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } }, padding: { top: 24 } }
  };

  const salesReceivedChartOptions = useMemo(() => ({
    ...barBase,
    xaxis: {
      categories: ['Total Sales', 'Received', 'Outstanding'],
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    colors: ['#059669', '#22c55e', '#ef4444'],
    tooltip: {
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) },
      custom: ({ dataPointIndex }: any) => {
        if (dataPointIndex === 1) {
          return `<div style="padding:8px 12px;font-family:inherit;background:#fff;min-width:200px;">
            <b style="color:#0f172a;font-size:13px;">Received</b><br/>
            <span style="color:#22c55e;font-weight:700;font-size:14px;">${Utils.formatNumber(amountReceived, true, false)}</span>
            <div style="margin-top:6px;font-size:11px;color:#64748b;">
              Fully Paid: <b style="color:#22c55e;">${Utils.formatNumber(arFullyPaidAmt, true, false)}</b><br/>
              Partially Paid (Received): <b style="color:#3b82f6;">${Utils.formatNumber(arPartiallyPaidReceivedAmt, true, false)}</b>
            </div>
          </div>`;
        }
        const labels = ['Total Sales', 'Received', 'Outstanding'];
        const values = [totalSales, amountReceived, outstandingAR];
        const colors = ['#059669', '#22c55e', '#ef4444'];
        return `<div style="padding:8px 12px;font-family:inherit;background:#fff;"><b style="color:#0f172a;font-size:13px;">${labels[dataPointIndex]}</b><br/><span style="color:${colors[dataPointIndex]};font-weight:700;font-size:14px;">${Utils.formatNumber(values[dataPointIndex], true, false)}</span></div>`;
      }
    }
  }), [totalSales, amountReceived, outstandingAR, arFullyPaidAmt, arPartiallyPaidReceivedAmt]);

  const salesReceivedChartSeries = useMemo(() => [{
    name: 'Amount',
    data: [totalSales, amountReceived, outstandingAR]
  }], [totalSales, amountReceived, outstandingAR]);

  const apNums: any = APDashboard.numbers?.dashboardNumbers || {};
  const totalPurchase = Number(apNums.totalAP || 0);
  const outstandingAP = Number(apNums.toPay || apNums.apUnpaid || 0);
  const apFullyPaidAmt = Number(apNums.apFullyPaid || 0);
  const apPartiallyPaidReceivedAmt = Number(apNums.apPartiallyPaidReceived || 0);
  const amountPaid = Number(apNums.apPaid || (apFullyPaidAmt + apPartiallyPaidReceivedAmt) || 0);

  const purchasePaidChartOptions = useMemo(() => ({
    ...barBase,
    xaxis: {
      categories: ['Total Purchase', 'Paid', 'Outstanding'],
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    colors: ['#059669', '#22c55e', '#ef4444'],
    tooltip: {
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) },
      custom: ({ dataPointIndex }: any) => {
        if (dataPointIndex === 1) {
          return `<div style="padding:8px 12px;font-family:inherit;background:#fff;min-width:200px;">
            <b style="color:#0f172a;font-size:13px;">Paid</b><br/>
            <span style="color:#22c55e;font-weight:700;font-size:14px;">${Utils.formatNumber(amountPaid, true, false)}</span>
            <div style="margin-top:6px;font-size:11px;color:#64748b;">
              Fully Paid: <b style="color:#22c55e;">${Utils.formatNumber(apFullyPaidAmt, true, false)}</b><br/>
              Partially Paid (Paid): <b style="color:#3b82f6;">${Utils.formatNumber(apPartiallyPaidReceivedAmt, true, false)}</b>
            </div>
          </div>`;
        }
        const labels = ['Total Purchase', 'Paid', 'Outstanding'];
        const values = [totalPurchase, amountPaid, outstandingAP];
        const colors = ['#059669', '#22c55e', '#ef4444'];
        return `<div style="padding:8px 12px;font-family:inherit;background:#fff;"><b style="color:#0f172a;font-size:13px;">${labels[dataPointIndex]}</b><br/><span style="color:${colors[dataPointIndex]};font-weight:700;font-size:14px;">${Utils.formatNumber(values[dataPointIndex], true, false)}</span></div>`;
      }
    }
  }), [totalPurchase, amountPaid, outstandingAP, apFullyPaidAmt, apPartiallyPaidReceivedAmt]);

  const purchasePaidChartSeries = useMemo(() => [{
    name: 'Amount',
    data: [totalPurchase, amountPaid, outstandingAP]
  }], [totalPurchase, amountPaid, outstandingAP]);

  const initials = (name?: string) =>
    (name || 'NA').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const avatarColors = ['#84cc16', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444'];

  const PerformerRow = ({ item, index, color }: { item: any; index: number; color: string }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        py: 0.65,
        px: 0.5,
        borderRadius: 2,
        transition: 'background-color .2s',
        '&:hover': { backgroundColor: alpha(color, 0.06) }
      }}
    >
      <Avatar
        sx={{
          width: 30, height: 30,
          backgroundColor: alpha(color, 0.15),
          color,
          fontWeight: 700,
          fontSize: '0.7rem'
        }}
      >
        {initials(item.customer?.name)}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontWeight: 600, color: TEXT, fontSize: '0.75rem' }} noWrap>
          {item.customer?.name || 'N/A'}
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.65rem' }}>
          {fmtSmart(item.totalAmount || 0)}
        </Typography>
      </Box>
      <Box textAlign="right">
        <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.75rem' }}>
          {fmtSmart(item.totalOutstanding || 0)}
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.6rem' }}>
          Outstanding
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, backgroundColor: BG, minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Header Row */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Hi {user?.name || 'there'} 👋
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>
            {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })} · {selectedAccount?.name || 'your account'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 38,
            px: 1.5,
            borderRadius: 999,
            backgroundColor: SURFACE,
            border: `1px solid ${BORDER}`,
            boxSizing: 'border-box'
          }}>
            <PeopleIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, lineHeight: 1 }}>Customers</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
              {ARDashboard.numbers?.dashboardNumbers?.totalCustomers || 0}
            </Typography>
          </Box>
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 38,
            px: 1.5,
            borderRadius: 999,
            backgroundColor: SURFACE,
            border: `1px solid ${BORDER}`,
            boxSizing: 'border-box'
          }}>
            <LocalShippingIcon sx={{ fontSize: 14, color: '#ec4899' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, lineHeight: 1 }}>Vendors</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
              {ARDashboard.numbers?.dashboardNumbers?.totalVendors || 0}
            </Typography>
          </Box>
          <Filters
            type={VocuherCategory.SALE}
            filterHandler={handleFilterChange}
            hideFilter={true}
            hidePartySearch={true}
            hideReset={true}
          />
          {selectedAccount?._id && <BusinessSummarySend accountId={selectedAccount._id} />}
          <Button
            onClick={() => navigate('/coa-dashboard')}
            startIcon={<InsightsIcon sx={{ color: '#fff !important' }} />}
            sx={{
              backgroundColor: '#059669',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              borderRadius: 999,
              border: '1px solid #059669',
              boxShadow: 'none',
              px: 1.75,
              py: 0.6,
              '& .MuiButton-startIcon': { mr: 0.75 },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': { backgroundColor: '#047857', borderColor: '#047857', boxShadow: '0 6px 16px rgba(5,150,105,0.3)', transform: 'translateY(-2px)', color: '#fff' },
              '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 4px rgba(5,150,105,0.2)' }
            }}
          >
            CoA Dashboard
          </Button>
          <IconButton
            onClick={() => fetchDashboardData(filterParams)}
            disabled={isLoading}
            sx={{
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: 2,
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            <RefreshIcon sx={{ fontSize: 20, color: TEXT }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Combined Metrics Row - 6 cards */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Sales"
            value={Utils.formatNumber(totalSales, true, false)}
            trend={ARDashboard.sales?.dashboardSales?.trend}
            icon={<TrendingUpIcon />}
            accent={ACCENT}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Received"
            value={Utils.formatNumber(amountReceived, true, false)}
            icon={<AccountBalanceWalletIcon />}
            accent="#22c55e"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Unpaid (AR)"
            value={Utils.formatNumber(outstandingAR, true, false)}
            icon={<AccessTimeIcon />}
            accent="#ef4444"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Purchases"
            value={Utils.formatNumber(totalPurchase, true, false)}
            trend={APDashboard.sales?.dashboardSales?.trend}
            icon={<ShoppingCartIcon />}
            accent="#f59e0b"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Paid"
            value={Utils.formatNumber(amountPaid, true, false)}
            icon={<PaymentIcon />}
            accent="#3b82f6"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Total Unpaid (AP)"
            value={Utils.formatNumber(outstandingAP, true, false)}
            icon={<AccessTimeIcon />}
            accent="#dc2626"
          />
        </Grid>
      </Grid>

      {/* Row 1: Trend chart + Aging donut */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Sales vs Purchase
              </Typography>
              <Chip
                size="small"
                icon={<NorthEastIcon sx={{ fontSize: 11, color: '#16a34a !important' }} />}
                label="Live"
                sx={{ backgroundColor: ACCENT_SOFT, color: '#365314', fontWeight: 600, fontSize: '0.62rem', height: 18, '& .MuiChip-label': { px: 0.75 } }}
              />
            </Stack>
            {isLoading && ARDashboard.sales?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            ) : trendCategories.length === 0 ? (
              <Box sx={{ height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: MUTED }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: MUTED }}>No transactions found</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: MUTED, mt: 0.5 }}>Try selecting a wider date range</Typography>
              </Box>
            ) : (
              <Chart options={salesChartOptions as any} series={salesChartSeries as any} type="bar" height={220} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Aging Analysis
              </Typography>
              <Box textAlign="right">
                <Typography sx={{ fontSize: '0.6rem', color: MUTED, lineHeight: 1 }}>Total</Typography>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.78rem', lineHeight: 1.3 }}>
                  {fmtShort(ARDashboard.aging?.dashboardAging?.agingValue || 0)}
                </Typography>
              </Box>
            </Stack>
            {isLoading && ARDashboard.aging?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            ) : agingChartSeries.length === 0 ? (
              <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>
                No aging data
              </Box>
            ) : (
              <Chart options={agingChartOptions} series={agingChartSeries} type="donut" height={220} />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 2: Sales/Purchase bars */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Sales vs Received
            </Typography>
            {isLoading && (ARDashboard.sales?.status !== ApiStatus.SUCCESS || ARDashboard.customerDue?.status !== ApiStatus.SUCCESS) ? (
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            ) : (
              <Chart options={salesReceivedChartOptions} series={salesReceivedChartSeries} type="bar" height={200} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Purchase vs Paid
            </Typography>
            {isLoading && (APDashboard.sales?.status !== ApiStatus.SUCCESS || APDashboard.customerDue?.status !== ApiStatus.SUCCESS) ? (
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            ) : (
              <Chart options={purchasePaidChartOptions} series={purchasePaidChartSeries} type="bar" height={200} />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 3: Top Customers + Top Vendors side by side */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Top Customers · Outstanding
            </Typography>
            <Box>
              {isLoading && ARDashboard.customerDue?.status !== ApiStatus.SUCCESS ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} height={42} sx={{ mb: 0.25 }} />)
              ) : (ARDashboard.customerDue?.dashboardCustomerDue?.list?.length || 0) === 0 ? (
                <Box sx={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                ARDashboard.customerDue?.dashboardCustomerDue?.list?.slice(0, 5).map((item: any, index: number) => (
                  <PerformerRow key={index} item={item} index={index} color={avatarColors[index % avatarColors.length]} />
                ))
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Top Vendors · Outstanding
            </Typography>
            <Box>
              {isLoading && APDashboard.customerDue?.status !== ApiStatus.SUCCESS ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} height={42} sx={{ mb: 0.25 }} />)
              ) : (APDashboard.customerDue?.dashboardCustomerDue?.list?.length || 0) === 0 ? (
                <Box sx={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                APDashboard.customerDue?.dashboardCustomerDue?.list?.slice(0, 5).map((item: any, index: number) => (
                  <PerformerRow key={index} item={item} index={index} color={avatarColors[(index + 2) % avatarColors.length]} />
                ))
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
